import { EditStructuredSectionItemModal } from '@/components/SectionStructureForm/EditStructuredSectionItemModal';
import { ComparisonItems } from '@/components/settings/Content/ComparisonItems.tsx';
import { ImagesList } from '@/components/settings/Content/ImagesList.tsx';
import { PressMentions } from '@/components/settings/Content/PressMentions.tsx';
import { Testimonials } from '@/components/settings/Content/Testimonials.tsx';
import { SettingTitle } from '@/components/settings/SettingsTitle.tsx';
import { AccountContentContext } from '@/contexts/AccountContentContext.tsx';
import { useDashboardContext } from '@/contexts/DashboardContext.tsx';
import { useSnackbarContext } from '@/contexts/SnackbarContext.tsx';
import { useHasFeature } from '@/hooks/useHasFeature.ts';
import CreateComparisonCategoryModal from '@/modals/CreateComparisonCategoryModal.tsx';
import ImageLibraryModal from '@/modals/ImageLibraryModal/ImageLibraryModal';
import * as Sentry from '@sentry/react';
import {
  AssetData,
  ContentItem,
  SectionTypeStructureFieldList,
  TestimonialsSectionItemData,
} from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { MoreVert } from 'iconoir-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { uid } from 'uid';

export const ContentTab = () => {
  const { show, hide } = useSnackbarContext();
  const hasComparisonListsFeature = useHasFeature('comparisonLists');
  const { accountConfig, updateAccountConfig, getSectionTypeConfig } =
    useDashboardContext();
  const { contents, createContentItem, updateContentItem, deleteContentItem } =
    useContext(AccountContentContext);

  const [selectImageModalOpen, setSelectImageModalOpen] = useState<string>();
  const [testimonialModalOpen, setTestimonialModalOpen] = useState(false);
  const [testimonial, setTestimonial] = useState<TestimonialsSectionItemData>();

  const [pressMentionModalOpen, setPressMentionModalOpen] = useState(false);
  const [pressMention, setPressMention] =
    useState<TestimonialsSectionItemData>();

  const [comparisonCategory, setComparisonCategory] =
    useState<QueryDocumentSnapshot<ContentItem, ContentItem>>();
  const [comparisonItem, setComparisonItem] =
    useState<QueryDocumentSnapshot<ContentItem, ContentItem>>();
  const [comparisonCategoryModalOpen, setComparisonCategoryModalOpen] =
    useState(false);
  const [comparisonItemModalOpen, setComparisonItemModalOpen] = useState(false);

  const comparisonCategories = useMemo(
    () => contents?.filter((c) => c.data().type === 'comparisonCategory'),
    [contents],
  );
  const comparisonItems = useMemo(
    () => contents?.filter((c) => c.data().type === 'comparisonItem'),
    [contents],
  );

  const comparisonListItemField = useMemo(
    () =>
      getSectionTypeConfig('comparisonList')?.structure.fields?.find(
        (f) => f.type === 'list' && f.name === 'items',
      ) as SectionTypeStructureFieldList | undefined,
    [getSectionTypeConfig],
  );

  const testimonialItemField = useMemo(
    () =>
      getSectionTypeConfig('testimonials')?.structure.fields?.find(
        (f) => f.type === 'list' && f.name === 'testimonials',
      ) as SectionTypeStructureFieldList | undefined,
    [getSectionTypeConfig],
  );

  const pressMentionItemField = useMemo(
    () =>
      getSectionTypeConfig('pressMentions')?.structure.fields?.find(
        (f) => f.type === 'list' && f.name === 'pressMentions',
      ) as SectionTypeStructureFieldList | undefined,
    [getSectionTypeConfig],
  );

  const onHeroImageUploaded = useCallback(
    (newFiles?: AssetData[]) => {
      updateAccountConfig({
        defaultHeroImages: [
          ...(accountConfig?.defaultHeroImages || []),
          ...(newFiles || []).map((a) => ({ ...a, id: uid(12) })),
        ],
      });
      show('Uploaded');
    },
    [accountConfig?.defaultHeroImages, show, updateAccountConfig],
  );

  const onHeroImageDeleted = useCallback(
    (image: AssetData) => {
      updateAccountConfig({
        defaultHeroImages: [
          ...(accountConfig?.defaultHeroImages || []).filter(
            (i) => i.id !== image.id,
          ),
        ],
      });
      show('Deleted');
    },
    [accountConfig?.defaultHeroImages, show, updateAccountConfig],
  );

  const onClientLogosUploaded = useCallback(
    (newFiles?: AssetData[]) => {
      updateAccountConfig({
        defaultClientLogos: [
          ...(accountConfig?.defaultClientLogos || []),
          ...(newFiles || []).map((a) => ({ ...a, id: uid(12) })),
        ],
      });
      show('Uploaded');
    },
    [accountConfig?.defaultClientLogos, show, updateAccountConfig],
  );

  const onClientLogoDeleted = useCallback(
    (image: AssetData) => {
      updateAccountConfig({
        defaultClientLogos: [
          ...(accountConfig?.defaultClientLogos || []).filter(
            (i) => i.id !== image.id,
          ),
        ],
      });
      show('Deleted');
    },
    [accountConfig?.defaultClientLogos, show, updateAccountConfig],
  );

  return (
    <div className={'mx-auto max-w-161 px-2.5'}>
      <EditStructuredSectionItemModal
        item={testimonial}
        field={testimonialItemField}
        open={testimonialModalOpen}
        onClose={() => {
          setTestimonial(undefined);
          setTestimonialModalOpen(false);
        }}
        onSave={(newTestimonial) => {
          const newTestimonials = [...(accountConfig?.testimonials ?? [])];
          const index = newTestimonials.findIndex(
            (t) => t.id === newTestimonial.id,
          );
          if (index !== -1) newTestimonials[index] = newTestimonial;
          else newTestimonials.push(newTestimonial);

          updateAccountConfig({
            testimonials: newTestimonials,
          });
          show(index === -1 ? 'Created' : 'Updated');
          setTestimonial(undefined);
          setTestimonialModalOpen(false);
        }}
      />
      <EditStructuredSectionItemModal
        item={pressMention}
        open={pressMentionModalOpen}
        field={pressMentionItemField}
        onClose={() => {
          setPressMention(undefined);
          setPressMentionModalOpen(false);
        }}
        onSave={(newPressMention) => {
          const newPressMentions = [...(accountConfig?.pressMentions ?? [])];
          const index = newPressMentions.findIndex(
            (t) => t.id === newPressMention.id,
          );
          if (index !== -1) newPressMentions[index] = newPressMention;
          else newPressMentions.push(newPressMention);

          updateAccountConfig({
            pressMentions: newPressMentions,
          });
          show(index === -1 ? 'Created' : 'Updated');
          setPressMention(undefined);
          setPressMentionModalOpen(false);
        }}
      />
      <CreateComparisonCategoryModal
        item={
          comparisonCategory
            ? { ...comparisonCategory.data(), id: comparisonCategory.id }
            : undefined
        }
        open={comparisonCategoryModalOpen}
        onClose={() => {
          setComparisonCategory(undefined);
          setComparisonCategoryModalOpen(false);
        }}
        onSave={async (newItem) => {
          return (
            comparisonCategory
              ? updateContentItem(comparisonCategory.id, newItem)
              : createContentItem({ ...newItem, type: 'comparisonCategory' })
          )
            .then(() => {
              setComparisonCategory(undefined);
              setComparisonCategoryModalOpen(false);
              show(!comparisonCategory ? 'Created' : 'Updated');
            })
            .catch((e) => {
              Sentry.captureException(e);
              show(e.toString(), 'warn');
            });
        }}
      />

      <EditStructuredSectionItemModal
        item={
          comparisonItem
            ? { ...comparisonItem.data(), id: comparisonItem.id }
            : undefined
        }
        field={comparisonListItemField}
        open={comparisonItemModalOpen}
        onClose={() => {
          setComparisonCategory(undefined);
          setComparisonItem(undefined);
          setComparisonItemModalOpen(false);
        }}
        onSave={async (newItem) => {
          return (
            comparisonItem
              ? updateContentItem(comparisonItem.id, {
                  ...newItem,
                  type: 'comparisonItem',
                })
              : createContentItem({
                  ...newItem,
                  type: 'comparisonItem',
                  category: comparisonCategory?.id,
                })
          )
            .then(() => {
              setComparisonCategory(undefined);
              setComparisonItem(undefined);
              setComparisonItemModalOpen(false);
              show(!comparisonItem ? 'Created' : 'Updated');
            })
            .catch((e) => {
              Sentry.captureException(e);
              show(e.toString(), 'warn');
            });
        }}
      />

      <ImageLibraryModal
        multi
        open={!!selectImageModalOpen}
        onClose={() => setSelectImageModalOpen(undefined)}
        onSelected={(image) => {
          const mode = selectImageModalOpen;
          setSelectImageModalOpen(undefined);

          if (mode === 'hero') {
            onHeroImageUploaded(image);
          } else if (mode === 'logo') {
            onClientLogosUploaded(image);
          }
        }}
      />
      <div className={'flex flex-col gap-5'}>
        <SettingTitle
          title="Content"
          description="Add your content, for us to use in your landing pages."
        />

        <div className={'flex flex-col gap-3'}>
          <div className={'flex flex-wrap items-start justify-between gap-4'}>
            <SettingTitle
              title="Hero images"
              description="Add your hero images."
            />
            <Button onClick={() => setSelectImageModalOpen('hero')}>
              Add Hero Image
            </Button>
          </div>
          <div className="w-full">
            <ImagesList
              images={accountConfig?.defaultHeroImages}
              onDeleteImageClick={onHeroImageDeleted}
            />
          </div>
        </div>

        <Separator />

        <div className={'flex flex-col gap-3'}>
          <div className={'flex flex-wrap items-start justify-between gap-4'}>
            <SettingTitle
              title="Client logos"
              description="Add your client logos."
            />
            <Button onClick={() => setSelectImageModalOpen('logo')}>
              Add Client Logo
            </Button>
          </div>
          <div className="w-full">
            <ImagesList
              images={accountConfig?.defaultClientLogos}
              onDeleteImageClick={onClientLogoDeleted}
            />
          </div>
        </div>

        <Separator />

        <div className={'flex flex-col gap-3'}>
          <div className={'flex flex-wrap items-start justify-between gap-4'}>
            <SettingTitle
              title="Testimonials"
              description="Add your company's testimonials."
            />
            <Button onClick={() => setTestimonialModalOpen(true)}>
              Add Testimonial
            </Button>
          </div>
          <div className="w-full">
            <Testimonials
              testimonials={accountConfig?.testimonials}
              onEditClick={(testimonial) => {
                setTestimonial(testimonial);
                setTestimonialModalOpen(true);
              }}
              onDeleteClick={(testimonial) => {
                updateAccountConfig({
                  testimonials: accountConfig?.testimonials?.filter(
                    (t) => t.id !== testimonial.id,
                  ),
                });
                show('Deleted');
              }}
            />
          </div>
        </div>

        <Separator />

        <div className={'flex flex-col gap-3'}>
          <div className={'flex flex-wrap items-start justify-between gap-4'}>
            <SettingTitle
              title="Press Mentions"
              description="Add your company's press mentions."
            />
            <Button onClick={() => setPressMentionModalOpen(true)}>
              Add Press Mention
            </Button>
          </div>
          <div className={'w-full'}>
            <PressMentions
              pressMentions={accountConfig?.pressMentions || []}
              onEditClick={(pressMention) => {
                setPressMention(pressMention);
                setPressMentionModalOpen(true);
              }}
              onDeleteClick={(pressMention) => {
                updateAccountConfig({
                  pressMentions: accountConfig?.pressMentions?.filter(
                    (t) => t.id !== pressMention.id,
                  ),
                });
                show('Deleted');
              }}
            />
          </div>
        </div>

        {hasComparisonListsFeature && (
          <>
            <Separator />

            <div className={'flex flex-col gap-3'}>
              <div
                className={'flex flex-wrap items-start justify-between gap-4'}
              >
                <SettingTitle
                  title="Comparison lists"
                  description="Add your comparison lists by category."
                />
                <Button onClick={() => setComparisonCategoryModalOpen(true)}>
                  Add Category
                </Button>
              </div>
              <div className={'w-full'}>
                {comparisonCategories?.map((category) => (
                  <dl
                    key={category.id}
                    className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6"
                  >
                    <div>
                      <div className="flex justify-between pt-6">
                        <dt className="flex flex-col font-medium text-gray-700 sm:w-64 sm:pr-6">
                          <span>{category.data().name}</span>
                          <span className="text-xs text-gray-500">
                            Category
                          </span>
                        </dt>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size={'sm'}
                            onClick={() => {
                              setComparisonCategory(category);
                              setComparisonItem(undefined);
                              setComparisonItemModalOpen(true);
                            }}
                          >
                            Add Item
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size={'icon'}
                                type={'button'}
                                variant={'outline'}
                              >
                                <MoreVert
                                  strokeWidth={3}
                                  className="h-4 w-4"
                                  width={20}
                                  height={20}
                                />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side={'bottom'} align={'end'}>
                              <DropdownMenuItem
                                onClick={() => {
                                  setComparisonCategory(category);
                                  setComparisonCategoryModalOpen(true);
                                }}
                              >
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                variant={'destructive'}
                                onClick={() => {
                                  deleteContentItem(category.id);
                                  show('Deleted!');
                                }}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <div className="w-full">
                        <ComparisonItems
                          items={
                            comparisonItems?.filter(
                              (c) => c.data().category === category.id,
                            ) || []
                          }
                          onEditClick={(item) => {
                            setComparisonCategory(category);
                            setComparisonItem(item);
                            setComparisonItemModalOpen(true);
                          }}
                          onDuplicateClick={(item) => {
                            const snack = show('Duplicating...');
                            createContentItem({
                              ...item.data(),
                              id: uid(12),
                            })
                              .then(() => {
                                hide(snack.id);
                                show('Duplicated');
                              })
                              .catch((e) => {
                                hide(snack.id);
                                Sentry.captureException(e);
                                show(e.toString(), 'warn');
                              });
                          }}
                          onDeleteClick={(item) => {
                            deleteContentItem(item.id);
                            show('Deleted');
                          }}
                        />
                      </div>
                    </div>
                  </dl>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
